/**
 * Management overlay API client.
 *
 * Thin typed wrappers around the backend management-company + invitation
 * routes. Uses the existing `axiosInstance` so the JWT, X-Domain header,
 * 401 handler and account-deactivated handler all continue to work
 * unchanged.
 *
 * Vocabulary: a "ManagementCompany" (legacy: Organization) is the optional
 * agency/label/team overlay; a "ManagementMember" is a seat inside it; a
 * "ManagementArtistLink" is the company↔artist allocation; an
 * "ArtistManagerAccess" is the per-slug manager grant.
 *
 * Wire paths (`/api/organizations/...`) and JSON keys (`organizationId`,
 * etc.) are PRESERVED for runtime stability.
 *
 * Legacy type/function names (Organization, OrganizationMember, ...) are
 * exported as `@deprecated` aliases of the new canonical names so existing
 * importers keep working.
 */

import axiosInstance from '../utils/axiosInstance';

// ─── Types ────────────────────────────────────────────────────────────────

/**
 * Management-company seat tier.
 * Wire/DB string `'manager'` represents the bottom tier and is rendered as
 * "Member" in the UI — see `lib/labels.ts`. Owner > Admin > Member.
 */
export type ManagementRole = 'owner' | 'admin' | 'manager';
export type ManagementCompanyStatus = 'active' | 'inactive' | 'suspended' | 'archived';
export type ManagementMemberStatus = 'active' | 'removed';
export type ManagementArtistLinkAccessLevel = 'owner' | 'manager';
export type ManagementArtistLinkStatus = 'active' | 'revoked';
export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';
/** Wire-stored invitation kind. `'organization'` = management-company, `'domain'` = artist-direct. */
export type InvitationKind = 'organization' | 'domain';

/** @deprecated Use {@link ManagementRole}. */
export type OrgRole = ManagementRole;
/** @deprecated Use {@link ManagementCompanyStatus}. */
export type OrgStatus = ManagementCompanyStatus;
/** @deprecated Use {@link ManagementMemberStatus}. */
export type OrgMemberStatus = ManagementMemberStatus;
/** @deprecated Use {@link ManagementArtistLinkAccessLevel}. */
export type OrgDomainAccessLevel = ManagementArtistLinkAccessLevel;
/** @deprecated Use {@link ManagementArtistLinkStatus}. */
export type OrgDomainLinkStatus = ManagementArtistLinkStatus;

export interface ManagementCompany {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  status: ManagementCompanyStatus;
  ownerUserId: string;
  /** Soft-delete tombstone. Present means the company is dead. */
  deletedAt?: string | null;
  deletedByUserId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ManagementCompanyWithRole extends ManagementCompany {
  myRole?: ManagementRole | null;
}

/** @deprecated Use {@link ManagementCompany}. */
export type Organization = ManagementCompany;
/** @deprecated Use {@link ManagementCompanyWithRole}. */
export type OrganizationWithRole = ManagementCompanyWithRole;

export interface ManagementMember {
  _id: string;
  organizationId: string;
  invitedBy?: string | null;
  userId:
    | string
    | {
        _id: string;
        username?: string;
        fullName?: string;
        email?: string;
        user_type?: string;
        domain?: string;
      };
  role: ManagementRole;
  status: ManagementMemberStatus;
  invitedAt?: string;
  joinedAt?: string;
  /** Slugs (with friendly names) this member is explicitly assigned to.
   *  Only populated for scoped managers; owners/admins have implicit access
   *  to every linked artist and the UI surfaces "All artists" instead. */
  artistAssignments?: Array<{ domainSlug: string; domainName?: string | null }>;
}

/** @deprecated Use {@link ManagementMember}. */
export type OrganizationMember = ManagementMember;

export interface ManagementArtistLink {
  _id: string;
  organizationId: string;
  domainSlug: string;
  /** Friendly artist name resolved from the domain owner / ArtistIdentity.
   *  Optional on the wire; UI should fall back to {@link domainSlug}. */
  domainName?: string | null;
  accessLevel: ManagementArtistLinkAccessLevel;
  status: ManagementArtistLinkStatus;
  linkedAt?: string;
}

/** @deprecated Use {@link ManagementArtistLink}. */
export type OrganizationDomainLink = ManagementArtistLink;

export interface Invitation {
  _id: string;
  organizationId: string;
  email: string;
  invitedBy?: string | null;
  role: ManagementRole;
  status: InvitationStatus;
  token?: string;
  expiresAt?: string;
  createdAt?: string;
  /** Slugs to auto-assign on acceptance (scoped seats only). */
  pendingAssignments?: string[];
}

export interface AccessibleDomain {
  slug: string;
  source: 'owned' | 'organization';
  organizationId?: string;
  organizationName?: string;
  accessLevel?: ManagementArtistLinkAccessLevel;
  role?: ManagementRole;
  /** 'artist' (default) | 'management' — drives switcher badge + console nav. */
  siteType?: 'artist' | 'management';
  /** True when this is the company's OWN management website. */
  ownWebsite?: boolean;
}

// ─── Management website ───────────────────────────────────────────────────

export interface ManagementWebsite {
  _id: string;
  slug: string;
  url: string;
  siteType: 'artist' | 'management';
  organizationId?: string;
  isPublished: boolean;
  publishedAt?: string | null;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  customDomain?: string | null;
  plan?: string;
}

/** The company's management website, or null when it has none. */
export async function getManagementWebsite(orgId: string): Promise<ManagementWebsite | null> {
  const res = await axiosInstance.get(`/api/organizations/${orgId}/website`);
  return res.data?.website || null;
}

/**
 * Create the company's management website (SUPER ADMIN ONLY — the backend
 * rejects every other caller).
 */
export async function createManagementWebsite(
  orgId: string,
  payload: { slug?: string } = {},
): Promise<ManagementWebsite> {
  const res = await axiosInstance.post(`/api/organizations/${orgId}/website`, payload);
  return res.data?.website;
}

// ─── Management Companies ─────────────────────────────────────────────────

export async function listMyManagementCompanies(): Promise<ManagementCompanyWithRole[]> {
  const res = await axiosInstance.get('/api/organizations/mine');
  return res.data?.organizations || [];
}
/** @deprecated Use {@link listMyManagementCompanies}. */
export const listMyOrganizations = listMyManagementCompanies;

export async function listAccessibleDomains(): Promise<AccessibleDomain[]> {
  const res = await axiosInstance.get('/api/organizations/me/accessible-domains');
  return res.data?.domains || [];
}

export async function getManagementCompany(id: string): Promise<ManagementCompany> {
  const res = await axiosInstance.get(`/api/organizations/${id}`);
  return res.data?.organization;
}
/** @deprecated Use {@link getManagementCompany}. */
export const getOrganization = getManagementCompany;

export async function createManagementCompany(payload: {
  name: string;
  slug: string;
  description?: string;
}): Promise<ManagementCompany> {
  const res = await axiosInstance.post('/api/organizations', payload);
  return res.data?.organization;
}
/** @deprecated Use {@link createManagementCompany}. */
export const createOrganization = createManagementCompany;

export async function updateManagementCompany(
  id: string,
  updates: Partial<Pick<ManagementCompany, 'name' | 'description' | 'status'>>,
): Promise<ManagementCompany> {
  const res = await axiosInstance.patch(`/api/organizations/${id}`, updates);
  return res.data?.organization;
}
/** @deprecated Use {@link updateManagementCompany}. */
export const updateOrganization = updateManagementCompany;

// ─── Lifecycle ────────────────────────────────────────────────────────────

/** Flip the company to INACTIVE. Reversible. Owner / super-admin only. */
export async function deactivateManagementCompany(id: string): Promise<ManagementCompany> {
  const res = await axiosInstance.post(`/api/organizations/${id}/deactivate`);
  return res.data?.organization;
}
/** @deprecated Use {@link deactivateManagementCompany}. */
export const deactivateOrganization = deactivateManagementCompany;

/** Flip the company back to ACTIVE. Owner / super-admin only. */
export async function reactivateManagementCompany(id: string): Promise<ManagementCompany> {
  const res = await axiosInstance.post(`/api/organizations/${id}/reactivate`);
  return res.data?.organization;
}
/** @deprecated Use {@link reactivateManagementCompany}. */
export const reactivateOrganization = reactivateManagementCompany;

export interface DeleteManagementCompanyResult {
  organization: ManagementCompany;
  cascade: {
    invitationsRevoked: number;
    domainLinksRevoked: number;
    membersRemoved:     number;
  };
}
/** @deprecated Use {@link DeleteManagementCompanyResult}. */
export type DeleteOrganizationResult = DeleteManagementCompanyResult;

/**
 * Soft-delete the company. Cascades: members→removed, links→revoked,
 * pending invitations→revoked. Owner / super-admin only. NOT reversible
 * from the UI; the doc is retained for audit only.
 */
export async function deleteManagementCompany(id: string): Promise<DeleteManagementCompanyResult> {
  const res = await axiosInstance.delete(`/api/organizations/${id}`);
  return {
    organization: res.data?.organization,
    cascade: res.data?.cascade || { invitationsRevoked: 0, domainLinksRevoked: 0, membersRemoved: 0 },
  };
}
/** @deprecated Use {@link deleteManagementCompany}. */
export const deleteOrganization = deleteManagementCompany;

// ─── Members ──────────────────────────────────────────────────────────────

export interface ArtistDirectManager {
  _id: string;
  domainSlug: string;
  /** Friendly artist name resolved from the domain owner. Optional. */
  domainName?: string | null;
  assignedAt?: string;
  userId: null | {
    _id: string;
    username?: string;
    fullName?: string;
    email?: string;
    user_type?: string;
    domain?: string;
  };
  addedByArtist: null | {
    _id: string;
    fullName?: string | null;
    username?: string | null;
    email?: string | null;
  };
}

export interface MembersResponse {
  members: ManagementMember[];
  artistDirectManagers: ArtistDirectManager[];
}

export async function listMembers(orgId: string): Promise<MembersResponse> {
  const res = await axiosInstance.get(`/api/organizations/${orgId}/members`);
  return {
    members: res.data?.members || [],
    artistDirectManagers: res.data?.artistDirectManagers || [],
  };
}

export async function updateMember(
  orgId: string,
  userId: string,
  updates: { role?: ManagementRole; status?: ManagementMemberStatus },
): Promise<ManagementMember> {
  const res = await axiosInstance.patch(`/api/organizations/${orgId}/members/${userId}`, updates);
  return res.data?.member;
}

export async function removeMember(orgId: string, userId: string): Promise<void> {
  await axiosInstance.delete(`/api/organizations/${orgId}/members/${userId}`);
}

// ─── Invitations ──────────────────────────────────────────────────────────

export async function listInvitations(orgId: string): Promise<Invitation[]> {
  const res = await axiosInstance.get(`/api/organizations/${orgId}/invitations`);
  // Backend persists the field as `invitedRole`; normalize to `role` for UI.
  const raw = res.data?.invitations || [];
  return raw.map((inv: any) => ({ ...inv, role: inv.role ?? inv.invitedRole }));
}

export interface CreateInvitationResult {
  /** True when the target email already had an account; access was granted
   *  directly without an invitation token. */
  granted: boolean;
  alreadyHadAccess?: boolean;
  /** Populated on the granted path. */
  member?: {
    _id: string;
    userId: string;
    email: string;
    fullName: string;
    role: ManagementRole;
    status: string;
  };
  createdAssignments?: Array<{ domainSlug: string; assignmentId: string }>;
  skippedAssignments?: string[];
  /** Populated on the legacy invitation-token path. */
  invitation?: Invitation;
  token?: string;
}

export async function createInvitation(
  orgId: string,
  payload: { email: string; role: ManagementRole; pendingAssignments?: string[] },
): Promise<CreateInvitationResult> {
  // Backend expects `invitedRole` (see invitationService.createInvitation).
  // For new accounts, returns the raw one-time token in `token`. For
  // already-registered accounts, returns `granted: true` with member +
  // assignment details and no token.
  const res = await axiosInstance.post(`/api/organizations/${orgId}/invitations`, {
    email: payload.email,
    invitedRole: payload.role,
    pendingAssignments: payload.pendingAssignments || [],
  });
  const data = res.data || {};
  if (data.granted) {
    return {
      granted: true,
      alreadyHadAccess: !!data.alreadyHadAccess,
      member: data.member,
      createdAssignments: data.createdAssignments,
      skippedAssignments: data.skippedAssignments,
    };
  }
  const inv = data.invitation;
  return {
    granted: false,
    invitation: inv ? { ...inv, role: inv.role ?? inv.invitedRole } : inv,
    token: data.token || '',
  };
}

export async function revokeInvitation(orgId: string, inviteId: string): Promise<void> {
  await axiosInstance.delete(`/api/organizations/${orgId}/invitations/${inviteId}`);
}

export async function remindInvitation(
  orgId: string,
  inviteId: string,
): Promise<{ invitation: Invitation; token: string }> {
  const res = await axiosInstance.post(`/api/organizations/${orgId}/invitations/${inviteId}/remind`);
  const inv = res.data?.invitation;
  return {
    invitation: inv ? { ...inv, role: inv.role ?? inv.invitedRole } : inv,
    token: res.data?.token || '',
  };
}

// ─── Invitation acceptance (invitee-side) ────────────────────────────────

export interface PendingInvitation {
  _id: string;
  organizationId: string;
  email: string;
  invitedRole: ManagementRole;
  status: InvitationStatus;
  expiresAt?: string;
  createdAt?: string;
}

/** List pending invitations addressed to the current user's email. */
export async function listMyPendingInvitations(): Promise<PendingInvitation[]> {
  const res = await axiosInstance.get('/api/invitations/mine');
  return res.data?.invitations || [];
}

/** Accept an invitation using its raw one-time token. */
export async function acceptInvitationByToken(token: string): Promise<{
  organizationId: string;
  role: ManagementRole;
  status: ManagementMemberStatus;
} | null> {
  const res = await axiosInstance.post('/api/invitations/accept', { token });
  return res.data?.membership ?? null;
}

// ─── Public invite landing (no auth) ───────────────────────────────

export interface InvitationPublicPreview {
  organizationName: string;
  organizationUsable: boolean;
  invitedRole: ManagementRole | '';
  email: string;
  expiresAt?: string;
  status: InvitationStatus;
  hasAccount: boolean;
}

/** Resolve an invite token to a safe preview. Reachable WITHOUT auth. */
export async function lookupInvitationPublic(token: string): Promise<InvitationPublicPreview> {
  const res = await axiosInstance.get(`/api/invitations/public/lookup/${encodeURIComponent(token)}`);
  return res.data?.invitation;
}

export interface AcceptWithSignupResult {
  token: string;
  user: {
    _id: string;
    username: string;
    fullName?: string;
    email: string;
    user_type: string;
    is_active: boolean;
    domain: string | null;
    hasCompletedOnboarding?: boolean;
    approvalStatus?: string;
  };
  membership: { organizationId: string; role: ManagementRole; status: ManagementMemberStatus };
}

/**
 * Create a manager account bound to an invite email and atomically accept
 * the invitation. Returns a normal login JWT + user so the caller can hand
 * straight off to `handleLogin`.
 */
export async function acceptInvitationWithSignup(payload: {
  token: string;
  fullName: string;
  password: string;
}): Promise<AcceptWithSignupResult> {
  const res = await axiosInstance.post('/api/invitations/public/accept-with-signup', payload);
  return res.data;
}

// ─── Management↔Artist Links (allocated artists / domains) ───────────────

export async function listDomainLinks(orgId: string): Promise<ManagementArtistLink[]> {
  const res = await axiosInstance.get(`/api/organizations/${orgId}/domain-links`);
  return res.data?.links || [];
}

export async function linkDomain(
  orgId: string,
  payload: { domainSlug: string; accessLevel?: ManagementArtistLinkAccessLevel },
): Promise<ManagementArtistLink> {
  const res = await axiosInstance.post(`/api/organizations/${orgId}/domain-links`, payload);
  return res.data?.link;
}

export async function unlinkDomain(orgId: string, slug: string): Promise<void> {
  await axiosInstance.delete(`/api/organizations/${orgId}/domain-links/${slug}`);
}

// ─── ArtistManagerAccess (per-link manager scoping) ──────────────────────
// Management-company admins/owners decide which seats can access each
// allocated artist. May also be sourced artist-direct.

export interface ArtistManagerAccess {
  _id: string;
  organizationId: string | null;
  domainSlug: string;
  status: 'active' | 'revoked';
  assignedBy?: string | null;
  assignedAt?: string;
  userId:
    | string
    | {
        _id: string;
        username?: string;
        fullName?: string;
        email?: string;
        user_type?: string;
      };
}

/** @deprecated Use {@link ArtistManagerAccess}. */
export type OrganizationManagerAssignment = ArtistManagerAccess;

export async function listLinkAssignments(
  orgId: string,
  slug: string,
): Promise<ArtistManagerAccess[]> {
  const res = await axiosInstance.get(
    `/api/organizations/${orgId}/domain-links/${slug}/assignments`,
  );
  return res.data?.assignments || [];
}

export async function assignLinkManager(
  orgId: string,
  slug: string,
  userId: string,
): Promise<ArtistManagerAccess> {
  const res = await axiosInstance.post(
    `/api/organizations/${orgId}/domain-links/${slug}/assignments`,
    { userId },
  );
  return res.data?.assignment;
}

export async function unassignLinkManager(
  orgId: string,
  slug: string,
  userId: string,
): Promise<void> {
  await axiosInstance.delete(
    `/api/organizations/${orgId}/domain-links/${slug}/assignments/${userId}`,
  );
}
